import { useState, useEffect, useCallback, useMemo } from "react";
import { useTenant } from "../../../hooks/useTenant";
import { useMembers } from "../../../hooks/useMembers";
import MembershipService from "../../../services/membership_service";
import ActivityService from "../../../services/activity_service";
import EmployeeService from "../../../services/employee_service";
import MemberMembershipService from "../../../services/member_membership_service";
import { INITIAL_FILTERS, INITIAL_ACTIVE_FILTERS } from "../constants";
import { SearchHelper } from "../../../utils/helpers";

/**
 * useCrm
 * Hook principal da página de CRM.
 * Carrega dados de referência e aplica filtros acumulativos (AND) sob demanda.
 * A busca só executa ao clicar em "Buscar" (não é em tempo real).
 */
const useCrm = () => {
    const { idTenant } = useTenant();
    const { members: allMembers, loading: membersLoading } = useMembers();

    // ── Estado Principal ──
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [activeFilters, setActiveFilters] = useState(INITIAL_ACTIVE_FILTERS);
    const [appliedActiveFilters, setAppliedActiveFilters] = useState(null);
    const [activeContracts, setActiveContracts] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(null); // Filtros que foram efetivamente aplicados

    // ── Dados de Referência (para popular selects) ──
    const [memberships, setMemberships] = useState([]);
    const [activities, setActivities] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    // ── Carga dos dados de referência (uma vez) ──
    useEffect(() => {
        if (!idTenant) return;

        const loadRefs = async () => {
            setLoadingRefs(true);
            try {
                const [membershipsData, activitiesData, employeesData] = await Promise.all([
                    MembershipService.getMemberships(idTenant),
                    ActivityService.getActivities(idTenant),
                    EmployeeService.getEmployees(idTenant),
                ]);
                setMemberships(membershipsData || []);
                setActivities(activitiesData || []);
                setEmployees(employeesData || []);
            } catch (error) {
                console.error("[useCrm] Erro ao carregar dados de referência:", error);
            } finally {
                setLoadingRefs(false);
            }
        };

        loadRefs();
    }, [idTenant]);

    // ── Handlers de Filtros ──
    const updateActiveFilter = useCallback((field, value) => {
        setActiveFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const updateFilter = useCallback((field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);

    const resetFilters = useCallback(() => {
        setFilters(INITIAL_FILTERS);
        setActiveFilters(INITIAL_ACTIVE_FILTERS);
        setAppliedActiveFilters(null);
        setActiveContracts({});
        setHasSearched(false);
        setAppliedFilters(null);
        setMembers([]);
    }, []);

    // ── Execução da busca (sob demanda) ──
    const executeSearch = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setHasSearched(true);
        setAppliedFilters({ ...filters });
        setAppliedActiveFilters({ ...activeFilters });

        try {
            setMembers(allMembers || []);

            const activeMemberships = await MemberMembershipService.getActiveForTenant(idTenant);
            const mmByMember = {};
            activeMemberships.forEach(data => {
                if (!mmByMember[data.idMember]) mmByMember[data.idMember] = [];
                mmByMember[data.idMember].push(data);
            });
            setActiveContracts(mmByMember);
        } catch (error) {
            console.error("[useCrm] Erro ao buscar membros:", error);
        } finally {
            setLoading(false);
        }
    }, [idTenant, allMembers, filters, activeFilters]);

    // ── Aplicação de filtros sobre os membros carregados ──
    const filteredMembers = useMemo(() => {
        if (!appliedFilters || members.length === 0) return [];

        let result = [...members];

        // 1. Busca rápida (nome, email, CPF, telefone)
        if (appliedFilters.search) {
            const normalized = SearchHelper.normalize(appliedFilters.search);
            const searchLower = appliedFilters.search.toLowerCase();
            const searchDigits = appliedFilters.search.replace(/\D/g, "");
            result = result.filter(m => {
                const searchName = m.searchName || SearchHelper.normalize(m.fullName || "");
                return searchName.includes(normalized) ||
                    (m.email || "").toLowerCase().includes(searchLower) ||
                    (searchDigits && (m.document || "").replace(/\D/g, "").includes(searchDigits)) ||
                    (searchDigits && (m.phone || "").replace(/\D/g, "").includes(searchDigits));
            });
        }

        // 2. Situação (status)
        if (appliedActiveFilters.status && appliedFilters.status) {
            result = result.filter(m => m.status === appliedFilters.status);
        }

        // 3. Sexo
        if (appliedActiveFilters.gender && appliedFilters.gender) {
            result = result.filter(m => m.gender === appliedFilters.gender);
        }

        // 4. Atividade / Modalidade
        if (appliedActiveFilters.activityId && appliedFilters.activityId) {
            const selectedActivity = activities.find(a => a.id === appliedFilters.activityId);
            if (selectedActivity) {
                result = result.filter(m => {
                    const memberActivities = [...(m.activityNames || []), ...(m.historicalActivityNames || [])];
                    return memberActivities.includes(selectedActivity.name);
                });
            }
        }

        // 5. Faixa Etária
        if (appliedActiveFilters.age && (appliedFilters.ageMin || appliedFilters.ageMax)) {
            const today = new Date();
            result = result.filter(m => {
                if (!m.birthDate) return false;
                const birth = new Date(m.birthDate + "T00:00:00");
                let age = today.getFullYear() - birth.getFullYear();
                const md = today.getMonth() - birth.getMonth();
                if (md < 0 || (md === 0 && today.getDate() < birth.getDate())) age--;
                if (appliedFilters.ageMin && age < parseInt(appliedFilters.ageMin)) return false;
                if (appliedFilters.ageMax && age > parseInt(appliedFilters.ageMax)) return false;
                return true;
            });
        }

        // 6. Professor
        if (appliedActiveFilters.employeeId && appliedFilters.employeeId) {
            result = result.filter(m => m.teacher?.uid === appliedFilters.employeeId);
        }

        // 7. Consultor (Venda)
        if (appliedActiveFilters.consultantId && appliedFilters.consultantId) {
            result = result.filter(m => m.consultant?.uid === appliedFilters.consultantId);
        }

        // 8. Data da Venda — range
        if (appliedActiveFilters.saleDate && (appliedFilters.saleStartDate || appliedFilters.saleEndDate)) {
            result = result.filter(m => {
                if (!m.firstPurchaseDate) return false;
                const purchaseDate = m.firstPurchaseDate instanceof Date
                    ? m.firstPurchaseDate.toISOString().slice(0, 10)
                    : String(m.firstPurchaseDate).slice(0, 10);
                if (appliedFilters.saleStartDate && purchaseDate < appliedFilters.saleStartDate) return false;
                if (appliedFilters.saleEndDate && purchaseDate > appliedFilters.saleEndDate) return false;
                return true;
            });
        }

        
        // 9. Filtros de Contrato (se carregados)
        if (appliedActiveFilters.membershipId || appliedActiveFilters.durationType || appliedActiveFilters.contractDate) {
            result = result.filter(m => {
                const contracts = activeContracts[m.id] || [];
                if (contracts.length === 0) return false;

                return contracts.some(c => {
                    let match = true;
                    if (appliedActiveFilters.membershipId && appliedFilters.membershipId) {
                        const targetId = c.idMembershipTarget || c.membershipId;
                        if (targetId !== appliedFilters.membershipId) match = false;
                    }
                    if (appliedActiveFilters.durationType && appliedFilters.durationType) {
                        if (c.durationType !== appliedFilters.durationType) match = false;
                    }
                    if (appliedActiveFilters.contractDate) {
                        const endD = c.endDate;
                        if (appliedFilters.contractStartDate && endD < appliedFilters.contractStartDate) match = false;
                        if (appliedFilters.contractEndDate && endD > appliedFilters.contractEndDate) match = false;
                    }
                    return match;
                });
            });
        }

        // Injetar dados de contrato para exibição na tabela
        result = result.map(m => {
            const contracts = activeContracts[m.id] || [];
            let latestContract = null;
            if (contracts.length > 0) {
                latestContract = contracts.reduce((latest, c) => (!latest || c.endDate > latest.endDate) ? c : latest, null);
            }
            return { 
                ...m, 
                latestContractEndDate: latestContract ? latestContract.endDate : null,
                latestContractName: latestContract ? latestContract.membershipName : null
            };
        });

        return result;
    }, [members, appliedFilters, appliedActiveFilters, activities, activeContracts]);


    return {
        // Dados
        members: filteredMembers,
        totalMembers: members.length,
        loading: loading || membersLoading,
        loadingRefs,
        hasSearched,

        // Filtros
        filters,
        activeFilters,
        updateActiveFilter,
        updateFilter,
        resetFilters,
        executeSearch,

        // Dados de Referência (para popular selects)
        memberships,
        activities,
        employees,
    };
};

export default useCrm;
